import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { toast } from "sonner";
import { APP_NAME, APP_FULL } from "@/lib/constants";
import { ArrowLeft, Images, Church, Calendar, Share2, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicAlbum() {
  const { slug } = useParams();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, photos: [], index: 0, title: "" });

  useEffect(() => {
    axios.get(`${API}/public/albums/${slug}`)
      .then((r) => setAlbum(r.data))
      .catch(() => setAlbum(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: album?.name || "Album Kegiatan", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link disalin ke clipboard");
      }
    } catch {}
  };

  const totalPhotos = useMemo(() => (album?.kegiatan || []).reduce((s, k) => s + (k.photos_base64?.length || 0), 0), [album]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-sm text-muted-foreground">Memuat album…</div>;
  if (!album) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <Images className="h-10 w-10 text-muted-foreground" />
      <div className="text-sm text-muted-foreground text-center">Album tidak ditemukan atau tidak dipublikasikan.</div>
      <Link to="/public"><Button variant="outline">Kembali ke Portal Publik</Button></Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="relative bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 papua-motif opacity-30" />
        <div className="relative max-w-5xl mx-auto px-6 py-12">
          <Link to="/public" className="text-xs text-white/70 hover:text-white flex items-center gap-1 mb-6" data-testid="link-back-public">
            <ArrowLeft className="h-3 w-3" /> Portal Publik
          </Link>
          <div className="text-xs tracking-[0.22em] uppercase text-secondary font-bold flex items-center gap-2">
            <Images className="h-3.5 w-3.5" /> Album Kegiatan
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mt-3 leading-tight" data-testid="album-title">{album.name}</h1>
          {album.description && <p className="text-sm md:text-base mt-3 max-w-2xl opacity-90">{album.description}</p>}
          <div className="text-xs text-white/70 mt-4 flex items-center gap-3 flex-wrap">
            {album.church_name && <span className="inline-flex items-center gap-1"><Church className="h-3 w-3" /> {album.church_name}</span>}
            <span>{album.kegiatan?.length || 0} kegiatan · {totalPhotos} foto</span>
            <button onClick={share} className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="btn-share-public">
              <Share2 className="h-3 w-3" /> Bagikan
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        {(!album.kegiatan || album.kegiatan.length === 0) ? (
          <div className="border border-dashed border-border rounded-lg p-12 text-center">
            <Camera className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <div className="text-sm text-muted-foreground">Album belum berisi foto.</div>
          </div>
        ) : (
          <div className="space-y-8">
            {album.kegiatan.map((k) => (
              <div key={k.id}>
                <div className="flex items-center gap-2 mb-3">
                  <div>
                    <div className="font-heading font-bold text-lg">{k.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      <Calendar className="h-3 w-3" />
                      {new Date(k.date).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}
                      {k.location && <span>· {k.location}</span>}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {(k.photos_base64 || []).map((src, i) => (
                    <button key={i} onClick={() => setLightbox({ open: true, photos: k.photos_base64, index: i, title: k.title })} className="aspect-square rounded overflow-hidden bg-muted group" data-testid={`pub-thumb-${k.id}-${i}`}>
                      <img src={src} alt={`${k.title} ${i+1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-border bg-muted/30 py-8 mt-8">
        <div className="max-w-5xl mx-auto px-6 text-center text-xs text-muted-foreground">
          <div>{APP_FULL}</div>
          <div className="mt-1">Dibagikan melalui {APP_NAME} — untuk jemaat &amp; keluarga besar GPDP Klasis Mimika.</div>
        </div>
      </footer>

      <PhotoLightbox
        open={lightbox.open}
        photos={lightbox.photos}
        initialIndex={lightbox.index}
        onClose={() => setLightbox({ ...lightbox, open: false })}
        title={lightbox.title}
      />
    </div>
  );
}
